import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getOrgDocuments, saveDocument, deleteDocument } from '../../api';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import Modal from '../../components/Modal';
import {
  DocumentTextIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const MAX_CHARS = 30000;

export default function DocumentsPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const org = organizations.find(o => o._id === orgId);

  const [scope, setScope] = useState('club');
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  const isOwnerOrLeader = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'leader';
  const canEditClub = isOwnerOrLeader;

  const loadDoc = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getOrgDocuments(orgId, scope);
      const docs = res.data;
      if (docs.length > 0) {
        setDoc(docs[0]);
        setTitle(docs[0].title);
        setContent(docs[0].content || '');
      } else {
        setDoc(null);
        setTitle(scope === 'club' ? 'Club Document' : 'Personal Document');
        setContent('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [orgId, scope]);

  useEffect(() => { loadDoc(); }, [loadDoc]);

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (content.length > MAX_CHARS) { setError(`Content exceeds ${MAX_CHARS} characters`); return; }
    setSaving(true);
    setError('');
    try {
      const res = await saveDocument({ organizationId: orgId, scope, title: title.trim(), content });
      setDoc(res.data);
      setSuccess('Document saved');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    try {
      await deleteDocument(doc._id);
      setDoc(null);
      setTitle(scope === 'club' ? 'Club Document' : 'Personal Document');
      setContent('');
      setShowDelete(false);
      setSuccess('Document deleted');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
      setShowDelete(false);
    }
  };

  const canEdit = scope === 'personal' || canEditClub;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Documents</h2>
          <div className="subtitle">{org?.name || 'Organization'} — Shared & Personal Documents</div>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`btn ${scope === 'club' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setScope('club')}
        >
          Club Document
        </button>
        <button
          className={`btn ${scope === 'personal' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setScope('personal')}
        >
          Personal Document
        </button>
      </div>

      {loading ? <Loading /> : (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Title</label>
            <input
              className="form-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={!canEdit}
              maxLength={100}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="form-label">
              Content
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                {content.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
              </span>
            </label>
            <textarea
              className="form-input"
              value={content}
              onChange={e => {
                if (e.target.value.length <= MAX_CHARS) setContent(e.target.value);
              }}
              disabled={!canEdit}
              rows={20}
              style={{ fontFamily: 'inherit', resize: 'vertical', minHeight: 300 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {doc && canEdit && (
                <button className="btn btn-danger" onClick={() => setShowDelete(true)}>
                  <TrashIcon className="icon" /> Delete
                </button>
              )}
            </div>
            {canEdit && (
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <DocumentTextIcon className="icon" /> {saving ? 'Saving...' : 'Save Document'}
              </button>
            )}
          </div>

          {doc && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
              Last updated: {new Date(doc.updatedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      <Modal isOpen={showDelete} onClose={() => setShowDelete(false)} title="Delete Document">
        <p>Are you sure you want to delete this document? This cannot be undone.</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={() => setShowDelete(false)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}
